import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '@/lib/api';
import { pageTitle } from '@/lib/seo';
import { Header } from '@/components/Header';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Lock, MessageSquare, ChevronUp } from 'lucide-react';

interface Board {
  id: string;
  slug: string;
  name: string;
  description?: string;
  isPrivate: boolean;
  postCount: number;
  created: string;
  totalVotes: number;
  postsThisWeek: number;
  openCount: number;
  underReviewCount: number;
  plannedCount: number;
  inProgressCount: number;
  topPosts: Array<{
    id: string;
    slug: string;
    title: string;
    score: number;
    status: string;
  }>;
}

function BoardCardSkeleton() {
  return (
    <div className="border rounded-xl overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-primary/30 to-primary/10" />
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-5 w-5 rounded-full" />
        </div>
        <Skeleton className="h-4 w-full" />
        <div className="grid grid-cols-4 gap-2 py-3 border-y">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="text-center space-y-1">
              <Skeleton className="h-6 w-8 mx-auto" />
              <Skeleton className="h-3 w-10 mx-auto" />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BoardCard({ board }: { board: Board }) {
  const hasRecentActivity = board.postsThisWeek > 0;

  return (
    <Link
      to={`/boards/${board.slug}`}
      className="group block"
    >
      <Card className="h-full overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
        {/* Gradient header strip */}
        <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60" />

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <CardTitle className="text-lg font-semibold group-hover:text-primary transition-colors">
              {board.name}
            </CardTitle>
            <div className="flex items-center gap-2 shrink-0">
              {hasRecentActivity && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
              {board.isPrivate && (
                <Badge variant="secondary" className="text-[11px]">
                  <Lock className="h-3 w-3 mr-1" />
                  Private
                </Badge>
              )}
            </div>
          </div>

          {board.description && (
            <CardDescription className="line-clamp-2 text-sm mb-4">
              {board.description}
            </CardDescription>
          )}

          {/* Status Counts Grid */}
          <div className="grid grid-cols-4 gap-2 py-3 border-y border-border">
            <div className="text-center">
              <div className="text-xl font-bold text-foreground">{board.openCount || 0}</div>
              <div className="text-[10px] text-muted-foreground">Open</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-foreground">{board.underReviewCount || 0}</div>
              <div className="text-[10px] text-muted-foreground">Review</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-foreground">{board.plannedCount || 0}</div>
              <div className="text-[10px] text-muted-foreground">Planned</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-foreground">{board.inProgressCount || 0}</div>
              <div className="text-[10px] text-muted-foreground">In Progress</div>
            </div>
          </div>

          {/* Top Posts with numbered badges */}
          {board.topPosts.length > 0 && (
            <div className="mt-4 space-y-2">
              {board.topPosts.map((post, index) => (
                <div
                  key={post.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <div className="flex items-center justify-center h-5 w-5 rounded-full bg-primary/10 text-primary text-xs font-semibold shrink-0">
                    {index + 1}
                  </div>
                  <span className="flex-1 truncate text-muted-foreground">{post.title}</span>
                  <div className="flex items-center gap-0.5 text-xs text-muted-foreground shrink-0">
                    <ChevronUp className="h-3 w-3" />
                    {post.score}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </Card>
    </Link>
  );
}

export default function BoardsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['boards'],
    queryFn: () => api.boards.list(),
    placeholderData: keepPreviousData,
  });

  const boards: Board[] = data?.boards || [];

  // Only show skeleton on initial load when we have no data
  const showSkeleton = isLoading && boards.length === 0;

  // Calculate aggregate stats
  const totalBoards = boards.length;
  const totalPosts = boards.reduce((sum, b) => sum + b.postCount, 0);
  const totalVotes = boards.reduce((sum, b) => sum + b.totalVotes, 0);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle('Feedback Boards')}</title>
      </Helmet>
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="mb-12 relative">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent rounded-3xl" />

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 py-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight">Feedback Boards</h1>
              <p className="text-muted-foreground mt-2 text-lg">
                Browse and contribute ideas to shape the future
              </p>
            </div>

            {/* Aggregate Stats */}
            {!showSkeleton && boards.length > 0 && (
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <div className="text-2xl font-bold">{totalBoards}</div>
                  <div className="text-xs text-muted-foreground">Boards</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{totalPosts}</div>
                  <div className="text-xs text-muted-foreground">Ideas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{totalVotes}</div>
                  <div className="text-xs text-muted-foreground">Votes</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Card>
            <CardHeader>
              <p className="text-destructive font-medium">Error loading boards</p>
              <p className="text-muted-foreground text-sm mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </CardHeader>
          </Card>
        )}

        {/* Loading State */}
        {showSkeleton && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <BoardCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!showSkeleton && !error && boards.length === 0 && (
          <Card className="text-center py-20">
            <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
            <p className="text-xl font-semibold">No feedback boards yet</p>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Create your first board to start collecting valuable feedback from your users
            </p>
          </Card>
        )}

        {/* Boards Grid */}
        {!showSkeleton && !error && boards.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {boards.map((board) => (
              <BoardCard key={board.id} board={board} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
